import React, { useState } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import tw from "twin.macro";
import { css } from "styled-components/macro"; //eslint-disable-line
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-7.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "images/svg-decorator-blob-8.svg";

const Subheading = tw(SubheadingBase)`mb-4 text-center`;
const Heading = tw(SectionHeading)`w-full`;
const Description = tw(SectionDescription)`w-full text-center`;

const Column = tw.div`flex flex-col items-center`;
const HeaderContent = tw.div``;

const FAQSContainer = tw.dl`mt-12 max-w-4xl relative`;
const FAQ = tw.div`cursor-pointer select-none mt-5 px-8 sm:px-10 py-5 sm:py-4 rounded-lg text-gray-800 hover:text-gray-900 bg-gray-200 hover:bg-gray-300 transition duration-300`;
const Question = tw.dt`flex justify-between items-center`;
const QuestionText = tw.span`text-lg lg:text-xl font-semibold`;
const QuestionToggleIcon = motion(styled.span`
  ${tw`ml-2 transition duration-300`}
  svg {
    ${tw`w-6 h-6`}
  }
`);
const Answer = motion(tw.dd`pointer-events-none text-sm sm:text-base leading-relaxed`);

const DecoratorBlob1 = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none -z-20 absolute right-0 top-0 h-56 w-56 opacity-15 transform translate-x-2/3 -translate-y-12 text-teal-400`}
`;
const DecoratorBlob2 = styled(SvgDecoratorBlob2)`
  ${tw`pointer-events-none -z-20 absolute left-0 bottom-0 h-64 w-64 opacity-15 transform -translate-x-2/3 text-primary-500`}
`;



export default ({
  subheading = "FAQS",
  heading = "Toronto Harbour Water Taxi – FAQs",
  description = "",
  faqs = [
    {
      question: "Where do you go?",
      answer:
        "Our Yellow Water Taxis depart from York and Queens Quay and service the Wards Island, Centre Island and Hanlan’s Point Ferry docks. We also provide service to several private marinas and yacht clubs also on the Toronto Islands."
    },
    {
      question: "What is the cost?",
      answer:
        "$13.00 per person. Debit and Credit transactions have a card fee added. Infants under 2 Years – Free. Bicycles, strollers and dogs – Free. Large freight $40-200. We accept Cash, Debit, Mastercard and Visa. Prices are per trip."
    },
    {
      question: "How do we get back from the island?",
      answer:
        "Passengers have two options to return from the island: 1. Take another Yellow Water Taxi back at any time from any of the ferry terminals for an additional fare. 2. Return with the scheduled City Ferry Service from any of the ferry terminals for no additional fare.  (Ferry schedules are available on the City of Toronto website)."
    },
    {
      question: "Can I bring a bicycle, stroller or dog on the Water Taxi?",
      answer:
        "Of course! And better yet, they’re free of charge."
    },
    {
      question: "How many passengers does a Water Taxi hold?",
      answer:
        "Our fleet of vessels can accommodate up to 12 or 24 passengers at once. For groups larger than 24, we can provide additional vessels with prior notice and availability."
    },
    {
      question: "Are Water Taxis available for private charter or prior reservation?",
      answer:
        "Our vessels are available by the hour, or by the day, at peak or off peak rates. Reservations are only available for groups greater than 12 passengers. Please call our customer service line (416-203-8294) or email info@towatertaxi.com for a quote."
    },
    {
      question: "It’s my first time to the Island, where should I go?",
      answer:
        "Centre Island is the most popular for tourist attractions and families with children. If you enjoy the atmosphere of a theme park, a hedge maze, a family friendly beach, various food options, and an unparalleled view of the Toronto Skyline, we highly recommend you start your day off here. For those wanting a more local experience, Ward’s Island is home to a small community of residents and their beautifully unique homes. It is also home to two lively establishments offering more exciting food options – the Riviera and the Wards Island Café. The quaint residential streets on Ward’s Island, and the adjacent Algonquin Island, immediately transport you into relaxing cottage country. Head south towards the open lake and you will stumble upon another inviting beach! And for those who just want to lounge on a picture perfect beach and let loose, Hanlan’s point is the place to be! Important to note that part of this beach is clothing optional and probably not as well suited for your family picnic."
    },
    {
      question: "Can I rent a Bicycle / Canoe / Kayak / SUP board on the island?",
      answer:
        "Yes you can! During peak season there are several companies that offer rentals for your activity of choice and are searchable online. Just ask one of our employees for more information."
    },
    {
      question: "What are your hours of operation?",
      answer:
        "Please note that our hours of operation can sometimes change depending on volume, weather, and the time of year.  If in doubt, call us to confirm service hours at 416-203-8294. Off peak season (May and October) hours are determined day by day. Peak Season (June 1st to September 30th) Sunday – Thursday: 10:00am – 8pm Friday – Saturday: 10:00am – Late"
    },
    {
      question: "Where can we park near your dock at York Street?",
      answer:
        "Paid parking is available at Ontario Square – Harbourfront Centre, 225 Queens Quay West (Queens Quay Ave. and Rees Street), a 3 minute walk from our dock."
    },
  ]
}) => {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);

  const toggleQuestion = questionIndex => {
    if (activeQuestionIndex === questionIndex) setActiveQuestionIndex(null);
    else setActiveQuestionIndex(questionIndex);
  };

  return (
    <Container>
      <ContentWithPaddingXl>
        <Column>
          <HeaderContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}
          </HeaderContent>
          <FAQSContainer>
            {faqs.map((faq, index) => (
              <FAQ
                key={index}
                onClick={() => {
                  toggleQuestion(index);
                }}
                className="group"
              >
                <Question>
                  <QuestionText>{faq.question}</QuestionText>
                  <QuestionToggleIcon
                    variants={{
                      collapsed: { rotate: 0 },
                      open: { rotate: -180 }
                    }}
                    initial="collapsed"
                    animate={activeQuestionIndex === index ? "open" : "collapsed"}
                    transition={{ duration: 0.02, ease: [0.04, 0.62, 0.23, 0.98] }}
                  >
                    <ChevronDownIcon />
                  </QuestionToggleIcon>
                </Question>
                <Answer
                  variants={{
                    open: { opacity: 1, height: "auto", marginTop: "16px" },
                    collapsed: { opacity: 0, height: 0, marginTop: "0px" }
                  }}
                  initial="collapsed"
                  animate={activeQuestionIndex === index ? "open" : "collapsed"}
                  transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                >
                  {faq.answer}
                </Answer>
              </FAQ>
            ))}
          </FAQSContainer>
        </Column>
      </ContentWithPaddingXl>
      <DecoratorBlob1/>
      <DecoratorBlob2 />
    </Container>
  );
};
